"use client";

import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useRef,
  useMemo,
  useCallback,
} from "react";
import { User } from "firebase/auth";
import {
  signInWithGoogle,
  signInWithEmail,
  createUserWithEmail,
  signOut as authSignOut,
  onAuthChange,
  sendMagicLinkEmail,
  signInWithMagicLink,
} from "@/lib/auth";
import { doc, getDoc, setDoc } from "firebase/firestore";
import { db } from "@/lib/firebase";
import { identifyUser, resetUser, captureException } from "@/lib/posthog";
import { setAuthHint } from "@/components/marketing/auth-redirect";
import { checkAppAccess, type AccessCheckResult } from "@/lib/access";
import { checkSuperAdmin } from "@/lib/permissions";
import type { SessionOrganization, SessionUser } from "@/types/auth";

interface AuthContextType {
  user: User | null;
  sessionUser: SessionUser | null;
  loading: boolean;
  authReady: boolean;
  organizations: SessionOrganization[];
  currentOrgId: string | null;
  currentOrg: SessionOrganization | null;
  // Access control
  hasAppAccess: boolean;
  accessLoading: boolean;
  accessReason: AccessCheckResult["reason"] | null;
  isSuperAdmin: boolean;
  recheckAccess: () => Promise<void>;
  // Auth methods
  signInWithGoogle: () => Promise<void>;
  signInWithEmail: (email: string, password: string) => Promise<void>;
  createUserWithEmail: (
    email: string,
    password: string,
    displayName: string,
  ) => Promise<void>;
  sendMagicLink: (email: string, redirectUrl?: string) => Promise<void>;
  signInWithMagicLink: (
    token: string,
    id: string,
  ) => Promise<{ redirectUrl?: string }>;
  signOut: () => Promise<void>;
  setCurrentOrgId: (orgId: string | null) => void;
  reloadOrganizations: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
}

// Helper to capture errors to PostHog
function captureError(error: unknown, context?: Record<string, unknown>) {
  console.error("[Auth]", error, context);
  captureException(error, context);
}

interface AuthProviderProps {
  children: React.ReactNode;
  initialSession?: SessionUser | null;
  initialOrganizations?: SessionOrganization[];
}

export function AuthProvider({
  children,
  initialSession = null,
  initialOrganizations = [],
}: AuthProviderProps) {
  const [user, setUser] = useState<User | null>(null);
  const [sessionUser, setSessionUser] = useState<SessionUser | null>(
    initialSession,
  );
  const [loading, setLoading] = useState(true);
  const [authReady, setAuthReady] = useState(false);
  const [organizations, setOrganizations] =
    useState<SessionOrganization[]>(initialOrganizations);
  const [currentOrgId, setCurrentOrgIdState] = useState<string | null>(
    initialOrganizations[0]?.id ?? null,
  );

  // Access control state
  const [hasAppAccess, setHasAppAccess] = useState(false);
  const [accessLoading, setAccessLoading] = useState(true);
  const [accessReason, setAccessReason] = useState<
    AccessCheckResult["reason"] | null
  >(null);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);

  const identifiedUserRef = useRef<string | null>(null);
  const hasLoadedOrganizationsRef = useRef(initialOrganizations.length > 0);
  const sessionRefreshTimerRef = useRef<NodeJS.Timeout | null>(null);
  const hasCheckedAccessRef = useRef(false);

  const currentOrg = useMemo(() => {
    if (!currentOrgId) {
      return null;
    }
    return organizations.find((org) => org.id === currentOrgId) ?? null;
  }, [currentOrgId, organizations]);

  // Refresh session cookie periodically to prevent expiration during active use
  useEffect(() => {
    if (!user) {
      // Clear any existing refresh timer when user logs out
      if (sessionRefreshTimerRef.current) {
        clearInterval(sessionRefreshTimerRef.current);
        sessionRefreshTimerRef.current = null;
      }
      return;
    }

    // Refresh session cookie every 4 hours (session lasts 5 days, but refresh proactively)
    const REFRESH_INTERVAL = 4 * 60 * 60 * 1000; // 4 hours in ms

    const refreshSession = async () => {
      // Transient network failures are common during background refresh
      // (laptop sleep/wake, flaky wifi). Retry once before reporting.
      const attempt = async () => {
        const idToken = await user.getIdToken(true); // Force refresh token
        await fetch("/api/auth/session", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ idToken }),
        });
      };

      // Two transient failure modes we never want to flag in error tracking:
      // 1. auth/network-request-failed — Firebase couldn't reach Google
      // 2. TypeError on fetch — browser couldn't reach our /api/auth/session
      // These are caused by laptop sleep/wake, offline blips, etc. Capturing
      // them just floods the exception dashboard.
      const isTransient = (err: any) =>
        err?.code === "auth/network-request-failed" ||
        err?.name === "TypeError";

      try {
        await attempt();
        console.log("[Auth] Session cookie refreshed");
      } catch (firstError: any) {
        if (!isTransient(firstError)) {
          captureError(firstError, { operation: "refresh_session_cookie" });
          return;
        }
        try {
          await new Promise((resolve) => setTimeout(resolve, 2000));
          await attempt();
          console.log("[Auth] Session cookie refreshed after retry");
        } catch (retryError: any) {
          if (isTransient(retryError)) {
            console.warn("[Auth] Session refresh failing — user may be offline");
            return;
          }
          captureError(retryError, {
            operation: "refresh_session_cookie",
            retried: true,
          });
        }
      }
    };

    // Set up interval to refresh session
    sessionRefreshTimerRef.current = setInterval(
      refreshSession,
      REFRESH_INTERVAL,
    );

    // Cleanup on unmount
    return () => {
      if (sessionRefreshTimerRef.current) {
        clearInterval(sessionRefreshTimerRef.current);
      }
    };
  }, [user]);

  useEffect(() => {
    const unsubscribe = onAuthChange(async (firebaseUser) => {
      processAuthStateChange(firebaseUser);
    });

    async function processAuthStateChange(firebaseUser: User | null) {
      setUser(firebaseUser);

      if (firebaseUser) {
        try {
          // Force refresh token to get latest custom claims (like superAdmin)
          const tokenResult = await firebaseUser.getIdTokenResult(true);
          setSessionUser({
            uid: firebaseUser.uid,
            email: firebaseUser.email,
            displayName: firebaseUser.displayName,
            photoURL: firebaseUser.photoURL,
            claims: tokenResult.claims ?? {},
          });

          // Mark auth as ready AFTER we've verified the token is available
          setAuthReady(true);
        } catch (tokenError) {
          captureError(tokenError, { operation: "fetch_id_token_claims" });
          setSessionUser({
            uid: firebaseUser.uid,
            email: firebaseUser.email,
            displayName: firebaseUser.displayName,
            photoURL: firebaseUser.photoURL,
            claims: {},
          });
          // Even with token error, mark auth as ready
          setAuthReady(true);
        }
      } else {
        setSessionUser(null);
        // No user = auth is ready (logged out state)
        setAuthReady(true);
      }

      if (firebaseUser) {
        // Only identify user in PostHog if we haven't already identified this user
        if (identifiedUserRef.current !== firebaseUser.uid) {
          identifyUser(firebaseUser.uid, {
            email: firebaseUser.email,
            name: firebaseUser.displayName,
            photoURL: firebaseUser.photoURL,
          });
          identifiedUserRef.current = firebaseUser.uid;
        }

        console.log("[Auth] User authenticated", {
          userId: firebaseUser.uid,
          email: firebaseUser.email,
        });

        // Wait for Firestore to fully propagate auth context
        // This prevents "Missing or insufficient permissions" errors when accessing /users/{uid}
        // Firestore needs time to recognize the user's auth token after Firebase Auth updates
        await new Promise((resolve) => setTimeout(resolve, 300));

        try {
          // Create or update user profile
          console.log(
            "[Auth] Creating/updating user profile for:",
            firebaseUser.uid,
          );
          const userRef = doc(db, "users", firebaseUser.uid);

          let userDoc = null;
          let retries = 0;
          const maxRetries = 2;

          // Retry logic for reading user doc to handle auth propagation delays
          while (retries < maxRetries) {
            try {
              userDoc = await getDoc(userRef);
              break; // Success, exit retry loop
            } catch (docError: any) {
              const isPermissionError =
                docError?.code === "permission-denied" ||
                docError?.message?.includes(
                  "Missing or insufficient permissions",
                );

              if (isPermissionError && retries < maxRetries - 1) {
                console.warn(
                  "[Auth] Permission error reading user doc, retrying in 500ms...",
                );
                retries++;
                await new Promise((resolve) => setTimeout(resolve, 500));
              } else {
                throw docError;
              }
            }
          }

          try {
            // userDoc should be set by the retry loop, but TypeScript needs assertion
            if (!userDoc || !userDoc.exists()) {
              console.log("[Auth] Creating new user profile");
              await setDoc(userRef, {
                displayName: firebaseUser.displayName,
                email: firebaseUser.email,
                photoURL: firebaseUser.photoURL,
                createdAt: new Date().toISOString(),
                lastSeen: new Date().toISOString(),
              });
              console.log("[Auth] New user profile created successfully");
            } else {
              const existingData = userDoc.data();

              // Check if we already have a cached avatar
              const hasCachedAvatar =
                existingData.cachedPhotoURL &&
                existingData.cachedPhotoURL.includes("storage.googleapis.com");

              // Only update Google photoURL if we don't have a cached version
              // or if the Google URL has changed
              if (
                !hasCachedAvatar ||
                (firebaseUser.photoURL &&
                  firebaseUser.photoURL !== existingData.originalPhotoURL)
              ) {
                console.log("[Auth] Updating user profile (photo)");
                await setDoc(
                  userRef,
                  {
                    displayName: firebaseUser.displayName,
                    photoURL: hasCachedAvatar
                      ? existingData.cachedPhotoURL
                      : firebaseUser.photoURL,
                    originalPhotoURL: firebaseUser.photoURL,
                    lastSeen: new Date().toISOString(),
                  },
                  { merge: true },
                );
              } else {
                // Just update last seen, keep cached avatar
                console.log("[Auth] Updating user profile (lastSeen)");
                await setDoc(
                  userRef,
                  {
                    displayName: firebaseUser.displayName,
                    lastSeen: new Date().toISOString(),
                  },
                  { merge: true },
                );
              }
            }
          } catch (profileError) {
            console.error(
              "[Auth] Error accessing/updating user profile:",
              profileError,
            );
            throw profileError;
          }
        } catch (error) {
          captureError(error, {
            operation: "user_profile_update",
            userId: firebaseUser.uid,
          });
        }

        if (!hasLoadedOrganizationsRef.current) {
          try {
            // Longer delay to ensure auth token is fully propagated to Firestore
            // Magic link/custom token auth needs time to propagate through Firestore's security layer
            // This prevents "Missing or insufficient permissions" errors on fresh sign-in
            await new Promise((resolve) => setTimeout(resolve, 500));

            console.log(
              "[Auth] Loading organizations for user:",
              firebaseUser.uid,
            );

            // Load user's organizations
            const { getUserOrganizations } = await import(
              "@/lib/organizations"
            );
            const userOrgs = (await getUserOrganizations(
              firebaseUser.uid,
            )) as SessionOrganization[];
            setOrganizations(userOrgs);
            hasLoadedOrganizationsRef.current = true;
            // Set auth hint for instant redirect on next visit
            setAuthHint(true);

            if (userOrgs.length > 0 && typeof window !== "undefined") {
              const pathMatch = window.location.pathname.match(/^\/@([^\/]+)/);
              if (!pathMatch) {
                setCurrentOrgIdState(userOrgs[0].id);
              }
            }

            // Check app access after orgs are loaded
            if (!hasCheckedAccessRef.current) {
              hasCheckedAccessRef.current = true;
              setAccessLoading(true);
              try {
                // Check super admin status first (force refresh to get latest claims)
                const superAdminStatus = await checkSuperAdmin(
                  firebaseUser,
                  true,
                );
                setIsSuperAdmin(superAdminStatus);

                // Run the access check
                const accessResult = await checkAppAccess(
                  firebaseUser.uid,
                  firebaseUser.email,
                  userOrgs.length,
                  superAdminStatus,
                );

                setHasAppAccess(accessResult.hasAccess);
                setAccessReason(accessResult.reason);

                console.log("[Auth] Access check completed", {
                  hasAccess: accessResult.hasAccess,
                  reason: accessResult.reason,
                });
              } catch (accessError) {
                captureError(accessError, {
                  operation: "check_app_access",
                  userId: firebaseUser.uid,
                });
                // Default to no access on error to be safe
                setHasAppAccess(false);
                setAccessReason("no_access");
              } finally {
                setAccessLoading(false);
              }
            }
          } catch (error) {
            captureError(error, {
              operation: "load_organizations",
              userId: firebaseUser.uid,
            });
            setAccessLoading(false);
          }
        }
      } else {
        // Reset PostHog when user logs out
        resetUser();
        identifiedUserRef.current = null;
        setOrganizations([]);
        setCurrentOrgIdState(null);
        hasLoadedOrganizationsRef.current = false;
        // Reset access state
        setHasAppAccess(false);
        setAccessLoading(false);
        setAccessReason(null);
        setIsSuperAdmin(false);
        hasCheckedAccessRef.current = false;
        // Clear auth hint for instant redirect optimization
        setAuthHint(false);
        console.log("[Auth] User signed out");
      }

      setLoading(false);
    }

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (!hasLoadedOrganizationsRef.current && initialOrganizations.length > 0) {
      hasLoadedOrganizationsRef.current = true;
    }
  }, [initialOrganizations]);

  useEffect(() => {
    if (!currentOrgId && initialOrganizations.length > 0) {
      setCurrentOrgIdState(initialOrganizations[0].id);
    }
  }, [initialOrganizations]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSignInWithGoogle = async () => {
    try {
      await signInWithGoogle();
    } catch (error) {
      // User-cancellation codes are not errors — lib/auth.ts already skips
      // captureException for them, but re-capturing here was inflating
      // PostHog's error tracking with the auth/popup-closed-by-user cluster
      // (LB-208). Mirror the same filter at this boundary, and let
      // capture_exceptions stay off the hook by still rethrowing so the page
      // can reset its spinner state.
      const code = (error as { code?: string } | null)?.code;
      const userCancelled =
        code === "auth/popup-closed-by-user" ||
        code === "auth/cancelled-popup-request" ||
        code === "auth/user-cancelled";
      if (!userCancelled) {
        captureError(error, { operation: "sign_in_google" });
      }
      throw error;
    }
  };

  const handleSignInWithEmail = async (email: string, password: string) => {
    try {
      await signInWithEmail(email, password);
    } catch (error) {
      captureError(error, { operation: "sign_in_email" });
      throw error;
    }
  };

  const handleCreateUserWithEmail = async (
    email: string,
    password: string,
    displayName: string,
  ) => {
    try {
      await createUserWithEmail(email, password, displayName);
    } catch (error) {
      console.error("[AuthContext] createUserWithEmail failed", error);
      captureError(error, { operation: "create_user_email" });
      throw error;
    }
  };

  const handleSendMagicLink = async (email: string, redirectUrl?: string) => {
    try {
      await sendMagicLinkEmail(email, redirectUrl);
    } catch (error) {
      captureError(error, { operation: "send_magic_link" });
      throw error;
    }
  };

  const handleSignInWithMagicLink = async (token: string, id: string) => {
    try {
      const result = await signInWithMagicLink(token, id);
      return { redirectUrl: result.redirectUrl };
    } catch (error) {
      captureError(error, { operation: "sign_in_magic_link" });
      throw error;
    }
  };

  const handleSignOut = async () => {
    try {
      await authSignOut();
      // Clear server-side session cookie
      await fetch("/api/auth/session", { method: "DELETE" }).catch(() => {
        // Ignore errors - best effort cleanup
      });
      setOrganizations([]);
      setCurrentOrgIdState(null);
      hasLoadedOrganizationsRef.current = false;
    } catch (error) {
      captureError(error, { operation: "sign_out" });
      throw error;
    }
  };

  const reloadOrganizations = async () => {
    if (user) {
      try {
        const { getUserOrganizations } = await import("@/lib/organizations");
        const userOrgs = (await getUserOrganizations(
          user.uid,
        )) as SessionOrganization[];
        setOrganizations(userOrgs);
        hasLoadedOrganizationsRef.current = true;

        // Set current org if not set
        if (userOrgs.length > 0 && !currentOrgId) {
          setCurrentOrgIdState(userOrgs[0].id);
        }

        console.log("[Auth] Organizations reloaded", {
          orgCount: userOrgs.length,
        });
      } catch (error) {
        captureError(error, {
          operation: "reload_organizations",
          userId: user.uid,
        });
        throw error;
      }
    }
  };

  const setCurrentOrgId = useCallback((orgId: string | null) => {
    setCurrentOrgIdState(orgId);
  }, []);

  // Recheck access - useful when user clicks "refresh" on pending page
  const recheckAccess = useCallback(async () => {
    if (!user) return;

    setAccessLoading(true);
    try {
      const superAdminStatus = await checkSuperAdmin(user);
      setIsSuperAdmin(superAdminStatus);

      const accessResult = await checkAppAccess(
        user.uid,
        user.email,
        organizations.length,
        superAdminStatus,
      );

      setHasAppAccess(accessResult.hasAccess);
      setAccessReason(accessResult.reason);

      console.log("[Auth] Access rechecked", {
        hasAccess: accessResult.hasAccess,
        reason: accessResult.reason,
      });
    } catch (error) {
      captureError(error, {
        operation: "recheck_app_access",
        userId: user.uid,
      });
    } finally {
      setAccessLoading(false);
    }
  }, [user, organizations.length]);

  const value = {
    user,
    sessionUser,
    loading,
    authReady,
    organizations,
    currentOrgId,
    currentOrg,
    // Access control
    hasAppAccess,
    accessLoading,
    accessReason,
    isSuperAdmin,
    recheckAccess,
    // Auth methods
    signInWithGoogle: handleSignInWithGoogle,
    signInWithEmail: handleSignInWithEmail,
    createUserWithEmail: handleCreateUserWithEmail,
    sendMagicLink: handleSendMagicLink,
    signInWithMagicLink: handleSignInWithMagicLink,
    signOut: handleSignOut,
    setCurrentOrgId,
    reloadOrganizations,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
